import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  FaCartPlus,
  FaHeart,
  FaSearch,
  FaUser,
  FaClipboardList,
  FaEllipsisV,
  FaBars,
  FaHome,
  FaQuestionCircle,
  FaBloggerB,
  FaInfoCircle,
  FaShoppingBag,
  FaTimes,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
} from "react-icons/fa";

import logo from "./Assets/buillogo6.png";
import { DataProvider } from "./ContextProvider";
import PincodeToCity from "./Location";
import LoginDialog from "./LoginDialog";
import { baseurl } from "../config/url";
import useDebounce from "../hooks/useDebounce";
import UserProfile from "./authcomponents/UserProfile";

/* ---------------------------------
   Small utilities (icons, sizes)
----------------------------------*/
const fileIconFor = (name = "") => {
  const n = name.toLowerCase();
  if (n.endsWith(".pdf")) return <FaFilePdf className="text-red-600" />;
  if (n.endsWith(".doc") || n.endsWith(".docx"))
    return <FaFileWord className="text-blue-600" />;
  if (n.endsWith(".xls") || n.endsWith(".xlsx"))
    return <FaFileExcel className="text-green-600" />;
  return <FaFileAlt className="text-slate-500" />;
};

const humanSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ---------------------------------
   Auth helpers
----------------------------------*/
const getAuthState = () => {
  try {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");
    return { isLoggedIn: Boolean(userId && token), userId, token };
  } catch (e) {
    return { isLoggedIn: false, userId: null, token: null };
  }
};

/* ---------------------------------
   Navbar Component
----------------------------------*/
const Navbar = () => {
  // Context (login/signup modal control)
  const {
    isLoginDialogOpen,
    issetLoginDialogOpen,
    setisSignup,
    isSignup,
    setshowotpcomponent,
  } = useContext(DataProvider);

  const navigate = useNavigate();

  /* -----------------
     Local state
  ------------------*/
  const [auth, setAuth] = useState(getAuthState());
  const [showProfile, setShowProfile] = useState(false);

  // Track previous login dialog open state (to detect close after login)
  const prevLoginOpenRef = useRef(isLoginDialogOpen);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);

  // Menus & Drawers
  const [anchorEl, setAnchorEl] = useState(null);
  const openUserMenu = Boolean(anchorEl);

  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const openMoreMenu = Boolean(moreAnchorEl);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Top notifications
  const [logoutMessage, setLogoutMessage] = useState("");

  // Rotating placeholders
  const placeholderOptions = useMemo(
    () => [
      "Cement",
      "Anchors",
      "Chemicals",
      "Light",
      "AQANO WATER FREE URINAL",
      "Paints",
      "Marble",
      "TMT",
      "Stone Chip",
      "Birla White Putty",
      "MO-P+ [410]",
      "Fly Ash",
    ],
    []
  );
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPlaceholderIndex((p) => (p + 1) % placeholderOptions.length);
    }, 3000);
    return () => clearInterval(id);
  }, [placeholderOptions.length]);

  /* -----------------
     Effects
  ------------------*/
  // Load search history
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("searchHistory") || "[]");
    setSearchHistory(Array.isArray(saved) ? saved : []);
  }, []);

  // Watch storage changes (if login/logout happens in another tab)
  useEffect(() => {
    const onStorage = () => setAuth(getAuthState());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // When LoginDialog closes, re-check auth from localStorage
  useEffect(() => {
    if (prevLoginOpenRef.current && !isLoginDialogOpen) {
      setAuth(getAuthState());
    }
    prevLoginOpenRef.current = isLoginDialogOpen;
  }, [isLoginDialogOpen]);

  /* -----------------
     Handlers – Login/Register
  ------------------*/
  const openLogin = useCallback(() => {
    setisSignup(false);
    setshowotpcomponent(false);
    issetLoginDialogOpen(true);
  }, [setisSignup, setshowotpcomponent, issetLoginDialogOpen]);

  const openRegister = useCallback(() => {
    setisSignup(true);
    setshowotpcomponent(false);
    issetLoginDialogOpen(true);
  }, [setisSignup, setshowotpcomponent, issetLoginDialogOpen]);

  /* -----------------
     Handlers – Menus
  ------------------*/
  const handleUserMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);

  const handleMoreClick = (e) => setMoreAnchorEl(e.currentTarget);
  const handleMoreClose = () => setMoreAnchorEl(null);

  /* -----------------
     Search logic
  ------------------*/
  const fetchSuggestions = async (value) => {
    if (!value) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const res = await axios.get(
        `${baseurl}/api/seller/product-suggestions?query=${encodeURIComponent(
          value
        )}`
      );
      setSuggestions(Array.isArray(res.data) ? res.data : []);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Suggestion fetch error:", err);
    }
  };

  const debouncedFetch = useDebounce(fetchSuggestions, 300);

  const handleSearch = async (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${baseurl}/api/seller/get-similar-products?name=${encodeURIComponent(
          term
        )}`
      );
      navigate("/similar-products", { state: { products: response.data } });

      const updated = [term, ...searchHistory.filter((t) => t !== term)].slice(
        0,
        10
      );
      setSearchHistory(updated);
      localStorage.setItem("searchHistory", JSON.stringify(updated));
    } catch (error) {
      console.error("Error fetching similar products:", error);
    } finally {
      setIsSearching(false);
      setSearchTerm("");
    }
  };

  const handleDeleteHistoryItem = (item) => {
    const updated = searchHistory.filter((t) => t !== item);
    setSearchHistory(updated);
    localStorage.setItem("searchHistory", JSON.stringify(updated));
  };

  /* -----------------
     Ask For Best Price – Modal state
  ------------------*/
  const [showBestPrice, setShowBestPrice] = useState(false);
  const [bpFiles, setBpFiles] = useState([]); // File[]
  const [bpPreviews, setBpPreviews] = useState([]); // {url,name,size,isImage}[]
  const [bpNote, setBpNote] = useState("");
  const [bpSubmitting, setBpSubmitting] = useState(false);
  const [bpStatus, setBpStatus] = useState(""); // success / error message

  const openBestPrice = () => {
    if (!auth.isLoggedIn) {
      openLogin();
      return;
    }
    setShowBestPrice(true);
  };

  const closeBestPrice = () => {
    bpPreviews.forEach(
      (p) => p.url && p.url.startsWith("blob:") && URL.revokeObjectURL(p.url)
    );
    setBpFiles([]);
    setBpPreviews([]);
    setBpNote("");
    setBpStatus("");
    setShowBestPrice(false);
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8);
    const previews = files.map((f) => {
      const isImg = /^image\//i.test(f.type);
      return {
        url: isImg ? URL.createObjectURL(f) : "",
        name: f.name,
        size: f.size,
        isImage: isImg,
      };
    });
    setBpFiles(files);
    setBpPreviews(previews);
    setBpStatus("");
  };

  const removeFileAt = (idx) => {
    const copyF = [...bpFiles];
    const copyP = [...bpPreviews];
    const [removed] = copyP.splice(idx, 1);
    copyF.splice(idx, 1);
    if (removed?.url && removed.url.startsWith("blob:"))
      URL.revokeObjectURL(removed.url);
    setBpFiles(copyF);
    setBpPreviews(copyP);
  };

  const submitBestPrice = async () => {
    if (!auth.isLoggedIn) {
      openLogin();
      return;
    }
    try {
      setBpSubmitting(true);
      setBpStatus("");
      const fd = new FormData();
      fd.append("userId", auth.userId);
      fd.append("note", bpNote);
      bpFiles.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${baseurl}/api/bestprice/request`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBpStatus(res?.data?.message || "Request submitted successfully.");
    } catch (err) {
      console.error(err);
      setBpStatus(
        err?.response?.data?.message ||
          "Failed to submit request. Please try again."
      );
    } finally {
      setBpSubmitting(false);
    }
  };

  /* -----------------
     Account actions
  ------------------*/
  const navigateToOrders = () => navigate("/orders");
  const openWishlist = () => navigate("/wishlist");

  const handleLogout = () => {
    try {
      localStorage.removeItem("userId");
      localStorage.removeItem("token");
      localStorage.removeItem("usercredentials");
    } catch {}
    setAuth(getAuthState());
    setLogoutMessage("You have successfully logged out.");
    handleUserMenuClose();
    setTimeout(() => {
      setLogoutMessage("");
      navigate("/");
    }, 1500);
  };

  /* -----------------
     Render helpers
  ------------------*/
  const TopStrip = (
    <div className="bg-gradient-to-r from-blue-500 to-blue-400 text-white py-2 px-2 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        {/* Left: Login/Register or quick-links when logged in */}
        <div className="flex items-center gap-2">
          {!auth.isLoggedIn ? (
            <>
              <button
                onClick={openLogin}
                className="text-sm sm:text-base font-semibold hover:underline"
              >
                Login
              </button>
              <span className="opacity-80">/</span>
              <button
                onClick={openRegister}
                className="text-sm sm:text-base font-semibold hover:underline"
              >
                Register
              </button>
            </>
          ) : (
            <div className="flex items-center gap-3 text-xs sm:text-sm">
              <button
                onClick={() => navigate("/profile")}
                className="hover:underline"
              >
                My Account
              </button>
              <button onClick={navigateToOrders} className="hover:underline">
                Orders
              </button>
              <button onClick={openWishlist} className="hover:underline">
                Wishlist
              </button>
              <button onClick={handleLogout} className="hover:underline">
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Right: Become a seller */}
        <div className="text-center">
          <span className="text-sm sm:text-base">
            Want to become a seller?{" "}
            <a
              href="https://buildproseller.ogcs.co.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-yellow-200 hover:underline font-semibold"
            >
              Sign up here
            </a>
          </span>
        </div>
      </div>
    </div>
  );

  const SearchBox = (
    <form onSubmit={handleSearch} className="w-full">
      <div className="flex items-center w-full rounded-lg">
        <div className="relative w-full">
          <input
            type="text"
            placeholder={`Search for "${placeholderOptions[placeholderIndex]}"`}
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              debouncedFetch(val);
            }}
            onFocus={() => {
              if (
                searchTerm.trim().length &&
                (suggestions.length || searchHistory.length)
              ) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
            className="w-full py-2 px-4 h-full rounded-l-lg text-sm sm:text-base text-gray-700 placeholder-gray-400 focus:outline-none border border-blue-600 focus:ring-2 focus:ring-blue-600"
          />

          {showSuggestions &&
            (suggestions.length > 0 || searchHistory.length > 0) && (
              <ul className="absolute top-full mt-1 z-20 w-full bg-white border border-gray-300 rounded-b-md max-h-64 overflow-y-auto shadow-md">
                {suggestions.map((item, i) => (
                  <li
                    key={`s-${i}`}
                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm sm:text-base"
                    onMouseDown={async () => {
                      setSearchTerm(item);
                      setShowSuggestions(false);
                      await handleSearch({ preventDefault: () => {} });
                    }}
                  >
                    {item}
                  </li>
                ))}

                {suggestions.length > 0 && searchHistory.length > 0 && (
                  <li className="px-4 py-1 text-[11px] sm:text-xs text-gray-400 border-t">
                    Recent Searches
                  </li>
                )}

                {searchHistory.map((item, i) => (
                  <li
                    key={`h-${i}`}
                    className="px-4 py-2 flex justify-between items-center hover:bg-red-50 cursor-pointer text-sm sm:text-base"
                  >
                    <span
                      onMouseDown={() => {
                        setSearchTerm(item);
                        setShowSuggestions(false);
                      }}
                    >
                      {item}
                    </span>
                    <button
                      className="text-red-500 text-base sm:text-lg ml-2 hover:text-red-700"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleDeleteHistoryItem(item);
                      }}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white hover:bg-blue-700 h-[38px] md:h-[42px] px-4 rounded-r-lg flex items-center justify-center"
          aria-label="Search"
          title="Search"
        >
          {isSearching ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
          ) : (
            <FaSearch className="text-sm sm:text-base" />
          )}
        </button>
      </div>
    </form>
  );

  const MobileDrawer = (
    <div className="w-[85vw] max-w-sm h-full bg-white">
      <div className="p-4 bg-gradient-to-r from-blue-400 to-blue-500 text-white">
        <div className="flex items-center gap-3">
          <img
            src={logo}
            alt="Logo"
            className="h-10 w-auto rounded bg-white/10 p-1"
            loading="lazy"
            width="140"
            height="56"
          />
          <div className="flex-1">
            <p className="text-[11px] sm:text-xs opacity-90">Welcome to</p>
            <h3 className="text-sm sm:text-base font-semibold">
              BuildPro OGCS
            </h3>
          </div>
        </div>
        <div className="mt-3">
          <PincodeToCity />
        </div>
        <a
          href="https://buildproseller.ogcs.co.in/"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 bg-yellow-300 text-gray-900 font-semibold px-3 py-1 rounded text-xs sm:text-sm"
        >
          <FaShoppingBag /> Become a Seller
        </a>
      </div>

      <div className="p-3">{SearchBox}</div>

      <Divider />
      <List>
        <ListItemButton
          onClick={() => {
            setMobileNavOpen(false);
            navigate("/");
          }}
        >
          <ListItemIcon>
            <FaHome />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </ListItemButton>

        <ListItemButton
          onClick={() => {
            setMobileNavOpen(false);
            navigate("/about");
          }}
        >
          <ListItemIcon>
            <FaInfoCircle />
          </ListItemIcon>
          <ListItemText primary="About Us" />
        </ListItemButton>

        {auth.isLoggedIn && (
          <>
            <ListItemButton
              onClick={() => {
                setMobileNavOpen(false);
                navigate("/orders");
              }}
            >
              <ListItemIcon>
                <FaClipboardList />
              </ListItemIcon>
              <ListItemText primary="Orders" />
            </ListItemButton>

            <ListItemButton
              onClick={() => {
                setMobileNavOpen(false);
                navigate("/cart");
              }}
            >
              <ListItemIcon>
                <FaCartPlus />
              </ListItemIcon>
              <ListItemText primary="Cart" />
            </ListItemButton>

            <ListItemButton
              onClick={() => {
                setMobileNavOpen(false);
                openWishlist();
              }}
            >
              <ListItemIcon>
                <FaHeart className="text-red-600" />
              </ListItemIcon>
              <ListItemText primary="Wishlist" />
            </ListItemButton>
          </>
        )}

        {/* Blogs & FAQ always in drawer */}
        <ListItemButton
          onClick={() => {
            setMobileNavOpen(false);
            navigate("/blogPage");
          }}
        >
          <ListItemIcon>
            <FaBloggerB />
          </ListItemIcon>
          <ListItemText primary="Blogs" />
        </ListItemButton>

        <ListItemButton
          onClick={() => {
            setMobileNavOpen(false);
            navigate("/faq");
          }}
        >
          <ListItemIcon>
            <FaQuestionCircle />
          </ListItemIcon>
          <ListItemText primary="FAQ" />
        </ListItemButton>
      </List>

      <Divider />
      {!auth.isLoggedIn ? (
        <div className="p-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              setMobileNavOpen(false);
              openLogin();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-800 text-white py-2 text-sm sm:text-base"
          >
            Login
          </button>
          <button
            onClick={() => {
              setMobileNavOpen(false);
              openRegister();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-200 text-gray-900 py-2 text-sm sm:text-base"
          >
            Register
          </button>
        </div>
      ) : (
        <div className="p-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              setMobileNavOpen(false);
              navigate("/profile");
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-800 text-white py-2 text-sm sm:text-base"
          >
            My Account
          </button>
          <button
            onClick={() => {
              setMobileNavOpen(false);
              handleLogout();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-200 text-gray-900 py-2 text-sm sm:text-base"
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Profile sidebar & Login dialog */}
      <UserProfile
        isSidebarOpen={showProfile}
        setSidebarOpen={setShowProfile}
        triggerLogin={openLogin}
      />
      <LoginDialog
        open={isLoginDialogOpen}
        onClose={() => issetLoginDialogOpen(false)}
        isSignup={isSignup}
        setisSignup={setisSignup}
      />

      {/* Top Strip (login-aware) */}
      {TopStrip}

      {logoutMessage && (
        <div className="bg-green-100 text-green-800 text-center py-2 text-sm sm:text-base">
          {logoutMessage}
        </div>
      )}

      {/* Main Nav */}
      <nav className="sticky top-0 z-40 bg-[#EFF6FF] px-4 flex flex-wrap items-center justify-between shadow-md">
        {/* Left cluster: menu + logo + quick CTA */}
        <div
          className="flex flex-row sm:items-center w-full sm:w-auto gap-2 sm:gap-4"
          style={{ justifyContent: "space-between" }}
        >
          {/* Mobile burger */}
          <button
            className="sm:hidden mr-1 p-2 rounded-md hover:bg-gray-100"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open Menu"
            title="Menu"
          >
            <FaBars className="text-xl" />
          </button>

          {/* Logo */}
          <Link
            to="/"
            className="group flex flex-col items-center px-2 sm:px-4 py-2 rounded-2xl  transition-all duration-300 no-underline decoration-none"
          >
            <img
              src={logo}
              alt="BuildPro OGCS"
              className="h-[45px] sm:h-[45px] transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              width="160"
              height="50"
              sizes="(max-width:640px) 48px, 45px"
            />
            <span className="mt-1 text-[11px] text-gray-600 text-center group-hover:text-blue-600 transition-colors">
              Building Supplies at Your Fingertips
            </span>
          </Link>

          {/* Quick CTA (desktop only) */}
          <Link
            to="/bulk-request"
            aria-label="Request a bulk quote"
            className="hidden sm:block group no-underline text-center inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:text-blue-600 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors font-[700]"

          >
            Request Quote <span><br /> For Bulk</span>
          </Link>
        </div>

        {/* Center cluster: location (desktop) + search */}
        <div className="hidden sm:flex sm:items-center gap-3 w-full sm:w-auto sm:mt-0 flex-grow sm:flex-grow-0 sm:ml-4">
          <div className="min-w-[220px]">
            <PincodeToCity />
          </div>
          <div className="flex-1 min-w-[240px] max-w-3xl w-full">
            {SearchBox}
          </div>
        </div>

        {/* Right cluster: cart + account + more */}
        <div className="flex items-center justify-end sm:mt-0 w-full lg:w-auto">
          <button
            onClick={() => navigate("/about")}
            className="hidden sm:inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition font-medium text-xs sm:text-sm"
            aria-label="About Us"
            title="About Us"
          >
            About Us
          </button>

          {/* Cart button — always visible.
              If not logged in: open login.
              If logged in: directly navigate to /cart.
          */}
          <button
            className="hidden md:flex relative bg-white border border-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-50 transition items-center gap-2 ml-2"
            onClick={() => {
              if (!auth.isLoggedIn) {
                openLogin();
              } else {
                navigate("/cart");
              }
            }}
            aria-label="Cart"
            title={auth.isLoggedIn ? "Go to Cart" : "Login to view cart"}
          >
            <span className="relative inline-flex">
              <FaCartPlus className="text-xl" />
            </span>
          </button>

          {/* Account (desktop) */}
          <button
            className="hidden sm:flex text-[#2563EB] p-3 rounded-full hover:bg-[#C7DFFF] transition items-center justify-center ml-[0.7rem]"
            onClick={handleUserMenuClick}
            aria-label="Account"
            title="Account"
          >
            <FaUser />
          </button>

          {/* More (desktop) */}
          <button
            className="hidden sm:flex text-[#2563EB] p-3 rounded-md hover:bg-[#C7DFFF] transition items-center justify-center"
            onClick={handleMoreClick}
            aria-label="More"
            title="More"
          >
            <FaEllipsisV />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        {MobileDrawer}
      </Drawer>

      {/* User Menu – login-aware options */}
      <Menu
        anchorEl={anchorEl}
        open={openUserMenu}
        onClose={handleUserMenuClose}
        PaperProps={{ style: { minWidth: 220 } }}
      >
        <MenuItem
          onClick={handleUserMenuClose}
          style={{
            justifyContent: "flex-end",
            color: "red",
            fontWeight: "bold",
            borderBottom: "1px solid #eee",
          }}
        >
          ✖
        </MenuItem>
        {auth.isLoggedIn ? (
          <>
            <MenuItem
              onClick={() => {
                setShowProfile(true);
                handleUserMenuClose();
              }}
            >
              Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                openBestPrice();
                handleUserMenuClose();
              }}
            >
              Ask for Best Price
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                openLogin();
                handleUserMenuClose();
              }}
            >
              Login
            </MenuItem>
            <MenuItem
              onClick={() => {
                openRegister();
                handleUserMenuClose();
              }}
            >
              Register
            </MenuItem>
          </>
        )}
      </Menu>

      {/* More Menu – Orders / Cart / Wishlist + Blogs / FAQ */}
      <Menu
        anchorEl={moreAnchorEl}
        open={openMoreMenu}
        onClose={handleMoreClose}
        PaperProps={{ style: { minWidth: 220 } }}
      >
        <MenuItem
          onClick={handleMoreClose}
          style={{
            justifyContent: "flex-end",
            color: "red",
            fontWeight: "bold",
            borderBottom: "1px solid #eee",
          }}
        >
          ✖
        </MenuItem>

        {auth.isLoggedIn && (
          <>
            <MenuItem
              onClick={() => {
                navigateToOrders();
                handleMoreClose();
              }}
            >
              Orders
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate("/cart");
                handleMoreClose();
              }}
            >
              Cart
            </MenuItem>
            <MenuItem
              onClick={() => {
                openWishlist();
                handleMoreClose();
              }}
            >
              Wishlist
            </MenuItem>
            <Divider />
          </>
        )}

        <MenuItem
          onClick={() => {
            navigate("/blogPage");
            handleMoreClose();
          }}
        >
          Blogs
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/faq");
            handleMoreClose();
          }}
        >
          FAQ
        </MenuItem>
      </Menu>

      {/* BEST PRICE MODAL */}
      {showBestPrice && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-3">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                Ask for Best Price
              </h3>
              <button
                onClick={closeBestPrice}
                className="p-2 rounded hover:bg-slate-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                  Add a note (optional)
                </label>
                <textarea
                  value={bpNote}
                  onChange={(e) => setBpNote(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm sm:text-base"
                  placeholder="Describe your requirement, quantity, brand preference, delivery city…"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                  Upload files
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={onPickFiles}
                  className="block w-full text-xs sm:text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-emerald-600 file:text-white hover:file:bg-emerald-700"
                />
                <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
                  Allowed: PDF, DOC/DOCX, XLS/XLSX, JPG/PNG. Max 8 files.
                </p>
              </div>

              {!!bpPreviews.length && (
                <div className="border rounded-lg p-3">
                  <p className="text-xs sm:text-sm font-medium text-slate-800 mb-2">
                    Selected files
                  </p>
                  <ul className="space-y-2 max-h-60 overflow-auto">
                    {bpPreviews.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        className="flex items-center gap-3 border rounded-md p-2"
                      >
                        {f.isImage ? (
                          <img
                            src={f.url}
                            alt={f.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 flex items-center justify-center rounded bg-slate-100">
                            {fileIconFor(f.name)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {f.name}
                          </p>
                          <p className="text-[11px] sm:text-xs text-slate-500">
                            {humanSize(f.size)}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFileAt(i)}
                          className="text-sm text-red-600 hover:text-red-700 px-2 py-1"
                          title="Remove"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {bpStatus && (
                <div
                  className={`text-sm px-3 py-2 rounded ${
                    /success|submitted/i.test(bpStatus)
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}
                >
                  {bpStatus}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t flex items-center justify-end gap-3">
              <button
                onClick={closeBestPrice}
                className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs sm:text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitBestPrice}
                disabled={bpSubmitting}
                className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-xs sm:text-sm"
              >
                {bpSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;